import React, { useMemo, useState } from 'react';
import {
  MdEventAvailable, MdCheckCircle, MdCancel, MdSchedule,
} from 'react-icons/md';
import { Card, StatCard, Badge, EmptyState } from '../../ui/Card';
import {
  dateLabel, monthKey, monthLabel, ATTENDANCE_LABELS, attendanceTone,
} from '../../ui/format';
import { useStudentData } from '../StudentDataContext';
import './pages.css';

export default function Attendance() {
  const { attendance } = useStudentData();
  const [month, setMonth] = useState('all');

  const months = useMemo(() => {
    const seen = [...new Set(attendance.map((a) => monthKey(a.date)).filter(Boolean))];
    return seen.sort((a, b) => b.localeCompare(a));
  }, [attendance]);

  const rows = useMemo(() => {
    const list = month === 'all' ? attendance : attendance.filter((a) => monthKey(a.date) === month);
    return [...list].sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
  }, [attendance, month]);

  const stats = useMemo(() => {
    const present = rows.filter((a) => a.status === 'present').length;
    const late = rows.filter((a) => a.status === 'late').length;
    const absent = rows.filter((a) => a.status === 'absent').length;
    // V2 records present / absent / late only; an excuse is a note on the
    // absence, shown on its row below.
    const counted = present + late + absent;
    return {
      present, absent, late,
      rate: counted ? Math.round(((present + late) / counted) * 100) : null,
    };
  }, [rows]);

  return (
    <div className="page">
      <h1 className="page-title">الحضور</h1>

      <div className="stat-grid">
        <StatCard
          icon={MdEventAvailable}
          tone={stats.rate === null ? 'info' : stats.rate >= 90 ? 'success' : stats.rate >= 75 ? 'warning' : 'danger'}
          value={stats.rate === null ? '—' : `${stats.rate}%`}
          label="نسبة الحضور"
        />
        <StatCard icon={MdCheckCircle} tone="success" value={stats.present} label="أيام الحضور" />
        <StatCard icon={MdCancel} tone="danger" value={stats.absent} label="أيام الغياب" />
        <StatCard icon={MdSchedule} tone="warning" value={stats.late} label="مرات التأخير" />
      </div>

      {months.length > 1 && (
        <div className="chip-row">
          <button
            type="button"
            className={`chip${month === 'all' ? ' is-active' : ''}`}
            onClick={() => setMonth('all')}
          >
            كل الأشهر
          </button>
          {months.map((m) => (
            <button
              key={m}
              type="button"
              className={`chip${month === m ? ' is-active' : ''}`}
              onClick={() => setMonth(m)}
            >
              {monthLabel(m)}
            </button>
          ))}
        </div>
      )}

      <Card title="سجل الحضور" icon={MdEventAvailable} padded={false}>
        {rows.length === 0 ? (
          <EmptyState icon={MdEventAvailable} title="لا يوجد سجل حضور" hint="ستظهر أيام الحضور والغياب هنا." />
        ) : (
          <ul className="row-list">
            {rows.map((a) => (
              <li key={`${a.date}-${a.section_id}`} className="row">
                <div className="row-main">
                  <div className="row-title">{dateLabel(a.date)}</div>
                  {(a.note || a.is_transferred) && (
                    <p className="row-sub">
                      {a.note}
                      {a.is_transferred ? ` (من شعبة سابقة: ${a.section_label || '—'})` : ''}
                    </p>
                  )}
                </div>
                <Badge tone={attendanceTone(a.status)}>
                  {ATTENDANCE_LABELS[a.status] || a.status}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
